import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import useLatestCallback from '@/hooks/useLatestCallback';
import { useOutletContext } from 'react-router-dom';
import {
  Activity, Server, Cpu, ArrowUpCircle, ArrowDownCircle,
  RefreshCw, Settings2, ChevronRight, Zap, Shield,
  Clock, AlertTriangle, CheckCircle2, XCircle, TrendingUp,
  TrendingDown, Minus, ChevronDown, ChevronUp, X
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '@/config/api';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
  ResponsiveContainer, Area, AreaChart, ReferenceLine
} from 'recharts';

// ─── Summary Cards ───────────────────────────────────────────
function SummaryCards({ summary }) {
  const cards = [
    {
      label: 'Total Services',
      value: summary.total_services,
      icon: Server,
      color: 'text-blue-600',
      bg: 'bg-blue-50',
    },
    {
      label: 'Healthy',
      value: summary.healthy_services,
      icon: CheckCircle2,
      color: 'text-emerald-600',
      bg: 'bg-emerald-50',
    },
    {
      label: 'Unhealthy',
      value: summary.unhealthy_services,
      icon: XCircle,
      color: summary.unhealthy_services > 0 ? 'text-red-600' : 'text-gray-400',
      bg: summary.unhealthy_services > 0 ? 'bg-red-50' : 'bg-gray-50',
    },
    {
      label: 'Total Workers',
      value: summary.total_workers,
      icon: Cpu,
      color: 'text-violet-600',
      bg: 'bg-violet-50',
    },
    {
      label: 'Auto-Scaling Active',
      value: summary.scaling_active,
      icon: Zap,
      color: 'text-amber-600',
      bg: 'bg-amber-50',
    },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
      {cards.map((c) => {
        const Icon = c.icon;
        return (
          <Card key={c.label} className="border-gray-100 shadow-sm" data-testid={`summary-${c.label.toLowerCase().replace(/\s/g, '-')}`}>
            <CardContent className="p-4 flex items-center gap-3">
              <div className={`${c.bg} p-2.5 rounded-lg`}>
                <Icon className={`h-5 w-5 ${c.color}`} />
              </div>
              <div>
                <p className="text-2xl font-bold tracking-tight">{c.value}</p>
                <p className="text-xs text-muted-foreground">{c.label}</p>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

// ─── Status Dot ──────────────────────────────────────────────
function StatusDot({ status }) {
  const cls = status === 'healthy'
    ? 'bg-emerald-500 shadow-emerald-400/50'
    : 'bg-red-500 shadow-red-400/50 animate-pulse';
  return <span className={`inline-block h-2.5 w-2.5 rounded-full shadow-md ${cls}`} />;
}

// ─── Service Grid ────────────────────────────────────────────
function ServiceGrid({ services, onSelect, onScale, scalingService, canScale = true }) {
  return (
    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
      {services.map((svc) => (
        <Card
          key={svc.service_name}
          className="border-gray-100 shadow-sm hover:shadow-md transition-shadow cursor-pointer group"
          data-testid={`service-card-${svc.service_name}`}
          onClick={() => onSelect(svc)}
        >
          <CardContent className="p-4">
            {/* Header row */}
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2.5">
                <StatusDot status={svc.status} />
                <div>
                  <p className="font-semibold text-sm leading-tight">{svc.service_name}</p>
                  <p className="text-xs text-muted-foreground">port {svc.port}</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {svc.enabled ? (
                  <Badge variant="outline" className="text-[10px] border-emerald-200 text-emerald-700 bg-emerald-50">AUTO</Badge>
                ) : (
                  <Badge variant="outline" className="text-[10px] border-gray-200 text-gray-500">MANUAL</Badge>
                )}
                <ChevronRight className="h-4 w-4 text-gray-300 group-hover:text-gray-500 transition-colors" />
              </div>
            </div>

            {/* Metrics */}
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="bg-gray-50 rounded-md py-1.5 px-1">
                <p className="text-xs text-muted-foreground">Response</p>
                <p className="text-sm font-semibold">
                  {svc.avg_response_ms != null ? `${svc.avg_response_ms}ms` : '--'}
                </p>
              </div>
              <div className="bg-gray-50 rounded-md py-1.5 px-1">
                <p className="text-xs text-muted-foreground">Workers</p>
                <p className="text-sm font-semibold">{svc.current_workers}/{svc.max_workers}</p>
              </div>
              <div className="bg-gray-50 rounded-md py-1.5 px-1">
                <p className="text-xs text-muted-foreground">Uptime</p>
                <p className="text-sm font-semibold">{svc.health_percentage}%</p>
              </div>
            </div>

            {/* Worker bar */}
            <div className="mt-3">
              <Progress
                value={(svc.current_workers / svc.max_workers) * 100}
                className="h-1.5"
              />
            </div>

            {/* Scale buttons */}
            <div className="flex items-center justify-between mt-3">
              <span className="text-[11px] text-muted-foreground">
                {svc.last_health_check ? `Checked ${timeAgo(svc.last_health_check)}` : 'No data yet'}
              </span>
              <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7"
                  disabled={!canScale || scalingService === svc.service_name || svc.current_workers <= svc.min_workers}
                  onClick={() => onScale(svc.service_name, 'down')}
                  data-testid={`scale-down-${svc.service_name}`}
                >
                  <ArrowDownCircle className="h-4 w-4 text-orange-500" />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7"
                  disabled={!canScale || scalingService === svc.service_name || svc.current_workers >= svc.max_workers}
                  onClick={() => onScale(svc.service_name, 'up')}
                  data-testid={`scale-up-${svc.service_name}`}
                >
                  <ArrowUpCircle className="h-4 w-4 text-blue-600" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ─── Events Timeline ─────────────────────────────────────────
function EventsTimeline({ events, filterService, onFilterChange }) {
  const serviceNames = [...new Set(events.map(e => e.service_name))].sort();

  return (
    <Card className="border-gray-100 shadow-sm">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-semibold">Scaling Events</CardTitle>
          <Select value={filterService} onValueChange={onFilterChange}>
            <SelectTrigger className="w-48 h-8 text-xs" data-testid="events-filter-service">
              <SelectValue placeholder="All services" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All services</SelectItem>
              {serviceNames.map(n => (
                <SelectItem key={n} value={n}>{n}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent className="px-4 pb-4">
        {events.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground" data-testid="events-empty">
            <Clock className="h-8 w-8 mx-auto mb-2 opacity-40" />
            <p className="text-sm">No scaling events recorded yet</p>
            <p className="text-xs mt-1">Events will appear here when services scale up or down</p>
          </div>
        ) : (
          <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
            {events.map((evt) => (
              <div
                key={evt.id}
                className="flex items-start gap-3 p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
                data-testid={`event-${evt.id}`}
              >
                {evt.direction === 'up' ? (
                  <div className="mt-0.5 bg-blue-100 p-1.5 rounded-md">
                    <TrendingUp className="h-4 w-4 text-blue-600" />
                  </div>
                ) : evt.direction === 'down' ? (
                  <div className="mt-0.5 bg-orange-100 p-1.5 rounded-md">
                    <TrendingDown className="h-4 w-4 text-orange-600" />
                  </div>
                ) : (
                  <div className="mt-0.5 bg-gray-100 p-1.5 rounded-md">
                    <Minus className="h-4 w-4 text-gray-500" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm">{evt.service_name}</span>
                    <Badge
                      variant="outline"
                      className={`text-[10px] ${
                        evt.event_type === 'scale_up'
                          ? 'border-blue-200 text-blue-700 bg-blue-50'
                          : evt.event_type === 'scale_down'
                          ? 'border-orange-200 text-orange-700 bg-orange-50'
                          : 'border-gray-200 text-gray-600'
                      }`}
                    >
                      {evt.event_type.replace('_', ' ').toUpperCase()}
                    </Badge>
                    {evt.from_workers != null && (
                      <span className="text-xs text-muted-foreground">
                        {evt.from_workers} &rarr; {evt.to_workers} workers
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5 truncate">{evt.trigger_reason}</p>
                </div>
                <span className="text-[11px] text-muted-foreground whitespace-nowrap">
                  {timeAgo(evt.created_at)}
                </span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Service Detail Drawer ───────────────────────────────────
function ServiceDetailDrawer({ service, open, onClose, token }) {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState('30');

  const fetchMetrics = useLatestCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/scaling/metrics/${service.service_name}?minutes=${period}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMetrics(data);
    } catch (err) {
      toast.error('Failed to load metrics');
    } finally {
      setLoading(false);
    }
  });

  useEffect(() => {
    if (open && service) fetchMetrics();
  }, [open, service, period, fetchMetrics]);

  if (!service) return null;

  const chartData = metrics?.metrics?.map((m) => ({
    time: new Date(m.recorded_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    response_ms: Math.round(m.response_ms * 100) / 100,
    healthy: m.is_healthy ? 1 : 0,
  })) || [];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="service-detail-dialog" aria-describedby="service-detail-desc">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <StatusDot status={service.status} />
            {service.service_name}
            <Badge variant="outline" className="text-xs ml-2">port {service.port}</Badge>
          </DialogTitle>
          <p id="service-detail-desc" className="sr-only">Service health metrics and scaling configuration details</p>
        </DialogHeader>

        {/* Quick stats */}
        <div className="grid grid-cols-4 gap-3 my-2">
          {[
            { label: 'Avg Response', value: `${service.avg_response_ms}ms` },
            { label: 'Workers', value: `${service.current_workers}/${service.max_workers}` },
            { label: 'Uptime', value: `${service.health_percentage}%` },
            { label: 'Priority', value: service.priority },
          ].map((s) => (
            <div key={s.label} className="bg-gray-50 rounded-lg p-3 text-center">
              <p className="text-lg font-bold">{s.value}</p>
              <p className="text-[11px] text-muted-foreground">{s.label}</p>
            </div>
          ))}
        </div>

        <Separator />

        {/* Period selector */}
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium">Response Time History</p>
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-32 h-8 text-xs" data-testid="metrics-period-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="10">Last 10 min</SelectItem>
              <SelectItem value="30">Last 30 min</SelectItem>
              <SelectItem value="60">Last 1 hour</SelectItem>
              <SelectItem value="360">Last 6 hours</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Chart */}
        {loading ? (
          <div className="h-52 flex items-center justify-center">
            <RefreshCw className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : chartData.length > 0 ? (
          <div className="h-52" data-testid="metrics-chart">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="colorResp" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#007AFF" stopOpacity={0.15} />
                    <stop offset="95%" stopColor="#007AFF" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="time" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} unit="ms" />
                <RTooltip
                  contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
                />
                {service.thresholds?.scale_up_response_ms && (
                  <ReferenceLine
                    y={service.thresholds.scale_up_response_ms}
                    stroke="#EF4444"
                    strokeDasharray="4 4"
                    label={{ value: 'Scale Up', position: 'right', fontSize: 10, fill: '#EF4444' }}
                  />
                )}
                <Area
                  type="monotone"
                  dataKey="response_ms"
                  stroke="#007AFF"
                  strokeWidth={2}
                  fill="url(#colorResp)"
                  dot={false}
                  activeDot={{ r: 4 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="h-52 flex items-center justify-center text-muted-foreground text-sm" data-testid="metrics-empty">
            No metrics collected for this period
          </div>
        )}

        {/* Stats summary */}
        {metrics?.stats && (
          <div className="grid grid-cols-4 gap-3 mt-1">
            {[
              { label: 'Avg', value: `${metrics.stats.avg_response_ms}ms` },
              { label: 'Min', value: `${metrics.stats.min_response_ms}ms` },
              { label: 'Max', value: `${metrics.stats.max_response_ms}ms` },
              { label: 'Uptime', value: `${metrics.stats.uptime_pct}%` },
            ].map((s) => (
              <div key={s.label} className="text-center">
                <p className="text-sm font-semibold">{s.value}</p>
                <p className="text-[10px] text-muted-foreground">{s.label}</p>
              </div>
            ))}
          </div>
        )}

        {/* Thresholds info */}
        {service.thresholds && (
          <>
            <Separator className="my-2" />
            <div>
              <p className="text-sm font-medium mb-2">Scaling Thresholds</p>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="bg-red-50 rounded-lg p-2.5">
                  <p className="text-red-700 font-medium">Scale Up</p>
                  <p className="text-red-600">&gt; {service.thresholds.scale_up_response_ms}ms response</p>
                  <p className="text-red-600">&gt; {service.thresholds.scale_up_error_rate}% error rate</p>
                </div>
                <div className="bg-green-50 rounded-lg p-2.5">
                  <p className="text-green-700 font-medium">Scale Down</p>
                  <p className="text-green-600">&lt; {service.thresholds.scale_down_response_ms}ms response</p>
                </div>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── Config Editor ───────────────────────────────────────────
function ConfigEditor({ configs, onSave, saving }) {
  const [editingService, setEditingService] = useState(null);
  const [form, setForm] = useState({});

  const startEdit = (cfg) => {
    setEditingService(cfg.service_name);
    setForm({
      enabled: cfg.enabled,
      min_workers: cfg.min_workers,
      max_workers: cfg.max_workers,
      scale_up_response_ms: cfg.scale_up_response_ms,
      scale_up_error_rate: cfg.scale_up_error_rate,
      scale_up_consecutive: cfg.scale_up_consecutive,
      scale_down_response_ms: cfg.scale_down_response_ms,
      scale_down_idle_minutes: cfg.scale_down_idle_minutes,
      scale_down_consecutive: cfg.scale_down_consecutive,
      priority: cfg.priority,
    });
  };

  const handleSave = () => {
    onSave(editingService, form);
    setEditingService(null);
  };

  return (
    <Card className="border-gray-100 shadow-sm">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <Settings2 className="h-4 w-4" />
          Scaling Configurations
        </CardTitle>
      </CardHeader>
      <CardContent className="px-0 pb-0">
        {/* Config edit dialog */}
        <Dialog open={!!editingService} onOpenChange={() => setEditingService(null)}>
          <DialogContent className="max-w-md" data-testid="config-edit-dialog" aria-describedby="config-edit-desc">
            <DialogHeader>
              <DialogTitle className="text-base">Edit: {editingService}</DialogTitle>
              <p id="config-edit-desc" className="sr-only">Edit scaling configuration for this service</p>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="flex items-center justify-between">
                <Label>Auto-Scaling Enabled</Label>
                <Switch
                  checked={form.enabled}
                  onCheckedChange={(v) => setForm({ ...form, enabled: v })}
                  data-testid="config-enabled-switch"
                />
              </div>
              <Separator />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Min Workers</Label>
                  <Input
                    type="number"
                    min={1}
                    value={form.min_workers}
                    onChange={(e) => setForm({ ...form, min_workers: parseInt(e.target.value) || 1 })}
                    className="h-9 mt-1"
                    data-testid="config-min-workers"
                  />
                </div>
                <div>
                  <Label className="text-xs">Max Workers</Label>
                  <Input
                    type="number"
                    min={1}
                    value={form.max_workers}
                    onChange={(e) => setForm({ ...form, max_workers: parseInt(e.target.value) || 1 })}
                    className="h-9 mt-1"
                    data-testid="config-max-workers"
                  />
                </div>
              </div>
              <Separator />
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Scale Up Triggers</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Response Time (ms)</Label>
                  <Input
                    type="number"
                    value={form.scale_up_response_ms}
                    onChange={(e) => setForm({ ...form, scale_up_response_ms: parseInt(e.target.value) || 500 })}
                    className="h-9 mt-1"
                    data-testid="config-scale-up-ms"
                  />
                </div>
                <div>
                  <Label className="text-xs">Error Rate (%)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={form.scale_up_error_rate}
                    onChange={(e) => setForm({ ...form, scale_up_error_rate: parseFloat(e.target.value) || 5 })}
                    className="h-9 mt-1"
                    data-testid="config-scale-up-error"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs">Consecutive Breaches</Label>
                <Input
                  type="number"
                  value={form.scale_up_consecutive}
                  onChange={(e) => setForm({ ...form, scale_up_consecutive: parseInt(e.target.value) || 4 })}
                  className="h-9 mt-1"
                  data-testid="config-scale-up-consecutive"
                />
              </div>
              <Separator />
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Scale Down Triggers</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Response Time (ms)</Label>
                  <Input
                    type="number"
                    value={form.scale_down_response_ms}
                    onChange={(e) => setForm({ ...form, scale_down_response_ms: parseInt(e.target.value) || 100 })}
                    className="h-9 mt-1"
                    data-testid="config-scale-down-ms"
                  />
                </div>
                <div>
                  <Label className="text-xs">Idle Minutes</Label>
                  <Input
                    type="number"
                    value={form.scale_down_idle_minutes}
                    onChange={(e) => setForm({ ...form, scale_down_idle_minutes: parseInt(e.target.value) || 5 })}
                    className="h-9 mt-1"
                    data-testid="config-scale-down-idle"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Consecutive Idles</Label>
                  <Input
                    type="number"
                    value={form.scale_down_consecutive}
                    onChange={(e) => setForm({ ...form, scale_down_consecutive: parseInt(e.target.value) || 10 })}
                    className="h-9 mt-1"
                    data-testid="config-scale-down-consecutive"
                  />
                </div>
                <div>
                  <Label className="text-xs">Priority</Label>
                  <Input
                    type="number"
                    value={form.priority}
                    onChange={(e) => setForm({ ...form, priority: parseInt(e.target.value) || 50 })}
                    className="h-9 mt-1"
                    data-testid="config-priority"
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditingService(null)}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving} data-testid="config-save-btn">
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Configs table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/60">
                <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Service</th>
                <th className="px-4 py-2.5 text-center font-medium text-muted-foreground">Enabled</th>
                <th className="px-4 py-2.5 text-center font-medium text-muted-foreground">Workers</th>
                <th className="px-4 py-2.5 text-center font-medium text-muted-foreground">Scale Up</th>
                <th className="px-4 py-2.5 text-center font-medium text-muted-foreground">Scale Down</th>
                <th className="px-4 py-2.5 text-center font-medium text-muted-foreground">Priority</th>
                <th className="px-4 py-2.5 text-right font-medium text-muted-foreground">Action</th>
              </tr>
            </thead>
            <tbody>
              {configs.map((cfg) => (
                <tr
                  key={cfg.service_name}
                  className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors"
                  data-testid={`config-row-${cfg.service_name}`}
                >
                  <td className="px-4 py-3 font-medium">{cfg.service_name}</td>
                  <td className="px-4 py-3 text-center">
                    {cfg.enabled ? (
                      <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[10px]">ON</Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px] text-gray-500">OFF</Badge>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center text-xs">{cfg.min_workers} - {cfg.max_workers}</td>
                  <td className="px-4 py-3 text-center text-xs">&gt; {cfg.scale_up_response_ms}ms</td>
                  <td className="px-4 py-3 text-center text-xs">&lt; {cfg.scale_down_response_ms}ms</td>
                  <td className="px-4 py-3 text-center">
                    <Badge variant="outline" className="text-[10px]">{cfg.priority}</Badge>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 text-xs"
                      onClick={() => startEdit(cfg)}
                      data-testid={`config-edit-${cfg.service_name}`}
                    >
                      Edit
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Time-ago helper ─────────────────────────────────────────
function timeAgo(isoStr) {
  if (!isoStr) return 'never';
  const diff = Date.now() - new Date(isoStr).getTime();
  const secs = Math.floor(diff / 1000);
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

// ─── Main Page ───────────────────────────────────────────────
export default function AutoScalingPage() {
  const { token } = useOutletContext();
  const [dashboard, setDashboard] = useState(null);
  const [configs, setConfigs] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [scalingService, setScalingService] = useState(null);
  const [savingConfig, setSavingConfig] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [eventFilter, setEventFilter] = useState('all');
  const intervalRef = useRef(null);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchAll = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const [dashRes, cfgRes, evtRes] = await Promise.all([
        axios.get(`${API}/scaling/dashboard`, { headers }),
        axios.get(`${API}/scaling/configs`, { headers }),
        axios.get(`${API}/scaling/events?limit=50`, { headers }),
      ]);
      setDashboard(dashRes.data);
      setConfigs(cfgRes.data);
      setEvents(evtRes.data);
    } catch (err) {
      if (!silent) toast.error('Failed to load scaling data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [headers]);

  useEffect(() => {
    fetchAll();
    intervalRef.current = setInterval(() => fetchAll(true), 30000);
    return () => clearInterval(intervalRef.current);
  }, [fetchAll]);

  const handleScale = async (serviceName, direction) => {
    setScalingService(serviceName);
    try {
      await axios.post(
        `${API}/scaling/trigger/${serviceName}/scale-${direction}`,
        {},
        { headers }
      );
      toast.success(`${serviceName} scaled ${direction}`);
      await fetchAll(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to scale ${direction}`);
    } finally {
      setScalingService(null);
    }
  };

  const handleSaveConfig = async (serviceName, data) => {
    setSavingConfig(true);
    try {
      await axios.put(`${API}/scaling/configs/${serviceName}`, data, { headers });
      toast.success(`Config updated for ${serviceName}`);
      await fetchAll(true);
    } catch (err) {
      toast.error('Failed to update config');
    } finally {
      setSavingConfig(false);
    }
  };

  const handleSelectService = (svc) => {
    setSelectedService(svc);
    setDetailOpen(true);
  };

  const filteredEvents = eventFilter === 'all'
    ? events
    : events.filter((e) => e.service_name === eventFilter);

  if (loading) {
    return (
      <div className="space-y-6" data-testid="scaling-loading">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-5 gap-4">
          {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-20" />)}
        </div>
        <div className="grid md:grid-cols-3 gap-3">
          {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-44" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="auto-scaling-page">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Zap className="h-6 w-6 text-amber-500" />
            Auto-Scaling Dashboard
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Monitor and manage service scaling across {dashboard?.summary?.total_services || 0} microservices
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => fetchAll(true)}
          disabled={refreshing}
          data-testid="refresh-dashboard-btn"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {dashboard?.mode === 'observe_only' && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="scaling-observe-only">
          Observe-only: health and response times are live; worker counts are managed by the hosting platform,
          so manual scale up / down is off here.
        </div>
      )}

      {/* Summary */}
      {dashboard?.summary && <SummaryCards summary={dashboard.summary} />}

      {/* Tabs */}
      <Tabs defaultValue="services" className="space-y-4">
        <TabsList className="bg-white border border-gray-200" data-testid="scaling-tabs">
          <TabsTrigger value="services" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700">
            <Server className="h-4 w-4 mr-1.5" />
            Services
          </TabsTrigger>
          <TabsTrigger value="events" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700">
            <Activity className="h-4 w-4 mr-1.5" />
            Events
          </TabsTrigger>
          <TabsTrigger value="config" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700">
            <Settings2 className="h-4 w-4 mr-1.5" />
            Configuration
          </TabsTrigger>
        </TabsList>

        <TabsContent value="services">
          {dashboard?.services && (
            <ServiceGrid
              services={dashboard.services}
              onSelect={handleSelectService}
              onScale={handleScale}
              scalingService={scalingService}
              canScale={dashboard?.mode !== 'observe_only'}
            />
          )}
        </TabsContent>

        <TabsContent value="events">
          <EventsTimeline
            events={filteredEvents}
            filterService={eventFilter}
            onFilterChange={setEventFilter}
          />
        </TabsContent>

        <TabsContent value="config">
          <ConfigEditor
            configs={configs}
            onSave={handleSaveConfig}
            saving={savingConfig}
          />
        </TabsContent>
      </Tabs>

      {/* Service Detail Drawer */}
      <ServiceDetailDrawer
        service={selectedService}
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        token={token}
      />

      {/* Footer info */}
      <div className="flex items-center justify-between text-xs text-muted-foreground pt-2 border-t border-gray-100">
        <span>Auto-refresh every 30s</span>
        {dashboard?.generated_at && (
          <span>Last updated: {new Date(dashboard.generated_at).toLocaleTimeString()}</span>
        )}
      </div>
    </div>
  );
}
